import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, Plus, CheckCircle2, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { euro } from "@/lib/format";
import { todayIso, fmtDay } from "@/lib/cantieri";
import { Button } from "@/components/ui/button";
import PaymentDialog from "@/components/agenda/PaymentDialog";

const shift = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

const Row = ({ o, today, onOpen, onPaid }) => {
  const late = o.data < today;
  return (
    <div className="flex items-center gap-3 px-4 py-3" data-testid={`due-row-${o.id}`}>
      <button type="button" onClick={() => onOpen(o)} className="flex-1 min-w-0 text-left">
        <div className="font-medium truncate">{o.titolo}</div>
        <div className={`text-xs ${late ? "text-[#DC2626] font-semibold" : "text-muted-foreground"}`}>{late ? "Scaduto il " : o.data === today ? "Oggi · " : ""}{fmtDay(o.data)}</div>
      </button>
      <div className="font-semibold text-sm tabular-nums">{o.importo ? euro(o.importo) : "—"}</div>
      <Button size="sm" variant="outline" onClick={() => onPaid(o)} data-testid={`due-paid-${o.id}`}><CheckCircle2 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Pagato</span></Button>
    </div>
  );
};

// Pagamenti da fare (scaduti + prossimi 30 giorni) in cima alla pagina «Pagamenti e incassi».
export const PaymentsDue = () => {
  const navigate = useNavigate();
  const [occs, setOccs] = useState(null);
  const [pags, setPags] = useState([]);
  const [dlg, setDlg] = useState(null);
  const today = todayIso();
  const load = useCallback(() => {
    api.get(`/pagamenti/occorrenze?da=${shift(today, -90)}&a=${shift(today, 30)}`).then((r) => setOccs(r.data.filter((o) => !o.pagato))).catch(() => setOccs([]));
    api.get("/pagamenti").then((r) => setPags(r.data)).catch(() => {});
  }, [today]);
  useEffect(() => { load(); }, [load]);
  const paid = async (o) => { await api.post(`/pagamenti/${o.pagamento_id}/pagato`, { data: o.data, pagato: true }); toast.success("Segnato come pagato"); load(); };
  const open = (o) => setDlg({ initial: pags.find((p) => p.id === o.pagamento_id) || null, occ: o });
  const total = (occs || []).reduce((a, o) => a + (Number(o.importo) || 0), 0);

  return (
    <section className="rounded-xl border border-border bg-card overflow-hidden" data-testid="payments-due">
      <div className="flex items-center gap-3 p-4 sm:p-5 border-b border-border flex-wrap">
        <span className="w-10 h-10 rounded-lg bg-[#DC2626] text-white flex items-center justify-center"><Wallet className="w-5 h-5" /></span>
        <div className="flex-1 min-w-[160px]">
          <h3 className="font-heading font-semibold">Pagamenti da fare</h3>
          <div className="text-xs text-muted-foreground">Scaduti e prossimi 30 giorni · totale <b className="text-foreground" data-testid="payments-due-total">{euro(total)}</b></div>
        </div>
        <Button size="sm" variant="ghost" onClick={() => navigate("/agenda")} data-testid="payments-due-agenda"><CalendarDays className="w-4 h-4 mr-1" /> Agenda</Button>
        <Button size="sm" onClick={() => setDlg({ initial: null, occ: null })} data-testid="payments-due-new"><Plus className="w-4 h-4 mr-1" /> Nuovo pagamento</Button>
      </div>
      <div className="divide-y divide-border max-h-96 overflow-y-auto">
        {(occs || []).map((o) => <Row key={o.id} o={o} today={today} onOpen={open} onPaid={paid} />)}
        {occs && occs.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Nessun pagamento da fare. Aggiungi rate, tasse o F24 per ricevere i promemoria.</div>}
      </div>
      <PaymentDialog open={!!dlg} initial={dlg?.initial} occ={dlg?.occ} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />
    </section>
  );
};

export default PaymentsDue;
